import {
	ButtonView,
    View,
	ViewCollection,
	submitHandler,
    FocusTracker,
	FocusCycler,
    KeystrokeHandler,
    LabeledFieldView,
    InputTextView,
	icons
} from "ckeditor5";

import '../../theme/formView.css';
import PreviewView from "./previewView.js";

/**
 * The youtube form view controller class.
 *
 * See {@link module:youtube/ui/formView~FormView}.
 *
 * @extends module:ui/view~View
 */
export default class FormView extends View {

    constructor(locale, dialog) {
        super( locale );
        const t = locale.t;

        /**
         * Dialog plugin provided by parent class.
         */
        this.dialog = dialog;

        /**
		 * Tracks information about DOM focus in the form.
		 *
		 * @readonly
		 * @member {module:utils/focustracker~FocusTracker}
		 */
		this.focusTracker = new FocusTracker();

        /**
		 * An instance of the {@link module:utils/keystrokehandler~KeystrokeHandler}.
		 *
		 * @readonly
		 * @member {module:utils/keystrokehandler~KeystrokeHandler}
		 */
		this.keystrokes = new KeystrokeHandler();

        /** Creating form elements */
        /**
		 * The YouTube Video Id input view.
		 *
		 * @member {module:ui/labeledfield/labeledfieldview~LabeledFieldView}
		 */
        this.videoIdInputView = this._createInput('YouTube Video Id')

        /**
		 * The video start time input view.
		 *
		 * @member {module:ui/labeledfield/labeledfieldview~LabeledFieldView}
		 */
        this.startTimeInputView = this._createInput('Start time')

        /**
		 * The video end time input view.
		 *
		 * @member {module:ui/labeledfield/labeledfieldview~LabeledFieldView}
		 */
        this.endTimeInputView = this._createInput('End time')

        /**
		 * The Save button view.
		 *
		 * @member {module:ui/button/buttonview~ButtonView}
		 */
		this.saveButtonView = this._createButton(
			t( 'Save' ),
			icons.check,
			'ck-button-save'
		);
		this.saveButtonView.type = 'submit';

		/**
		 * The Cancel button view.
		 *
		 * @member {module:ui/button/buttonview~ButtonView}
		 */
		this.cancelButtonView = this._createButton(
			t( 'Cancel' ),
			icons.cancel,
			'ck-button-cancel',
			'cancel'
		);

        /**
		 * A dynamic collection view that allows updating to show video preview.
		 *
		 * @member {module:ui/viewcollection~ViewCollection}
		 */
        this.resultView = this.createCollection();
        
		/**
		 * A collection of child views in the form.
		 *
		 * @readonly
		 * @type {module:ui/viewcollection~ViewCollection}
		 */
		this.children = this._createFormChildren();

        /**
		 * A collection of views that can be focused in the form.
		 *
		 * @readonly
		 * @protected
		 * @member {module:ui/viewcollection~ViewCollection}
		 */
		this._focusables = new ViewCollection();

		/**
		 * Helps cycling over {@link #_focusables} in the form.
		 *
		 * @readonly
		 * @protected
		 * @member {module:ui/focuscycler~FocusCycler}
		 */
		this._focusCycler = new FocusCycler( {
			focusables: this._focusables,
			focusTracker: this.focusTracker,
			keystrokeHandler: this.keystrokes,
			actions: {
				// Navigate form fields backwards using the Shift + Tab keystroke.
				focusPrevious: 'shift + tab',

				// Navigate form fields forwards using the Tab key.
				focusNext: 'tab'
			}
		} );

		const classList = [ 'ck', 'ck-youtube-form', 'ck-responsive-form' ];

        /**
		 * The YouTube video form view.
		 *
		 * @member {module:ui/view~View}
		 */
        const formView = new View ( locale );
        formView.setTemplate( {
			tag: 'form',

			attributes: {
				class: classList,
				tabindex: '-1'
			},

			children: this.children
		} );

        this.setTemplate( {
            tag: 'div',
            children: [
                formView,
                { 
                    tag: "div",
                    children: this.resultView,
                }
            ]
        })
    }

    /**
	 * @inheritDoc
	 */
    render() {
        super.render();

        submitHandler( {
            view: this
        } );

        // Excluded resultView from focusable elements
        const childViews = [
            this.videoIdInputView,
            this.startTimeInputView,
            this.endTimeInputView,
			this.saveButtonView,
			this.cancelButtonView,
        ];

        childViews.forEach( v => {
            // Register the view as focusable.
            this._focusables.add( v );

            // Register the view in the focus tracker.
            this.focusTracker.add( v.element );
        } );

        // Start listening for the keystrokes coming from #element.
        this.keystrokes.listenTo( this.element );
    }


    /**
	 * Focuses the fist {@link #_focusables} in the form.
	 */
	focus() {
		this._focusCycler.focusFirst();
	}

    /**
	 * Creates a labeled input view.
	 *
	 * @private
	 * @param {String} label The input label.
	 * @returns {module:ui/labeledfield/labeledfieldview~LabeledFieldView} Labeled field view instance.
	 */
    _createInput( label ) {
        const t = this.locale.t;
        const labeledInput = new LabeledFieldView(
            this.locale,
            this._createLabeledInputText
        );

        labeledInput.label = t(label)

        // Added label class to each input for styling
        const transformedLabel = label.toLowerCase().replace(" ", "-")
        labeledInput.class = `ck-input-${transformedLabel}`
        
		return labeledInput;
    }

    /**
	 * Creates a button view.
	 *
	 * @private
	 * @param {String} label The button label.
	 * @param {String} icon The button icon.
	 * @param {String} className The additional button CSS class name.
	 * @param {String} [eventName] An event name that the `ButtonView#execute` event will be delegated to.
	 * @returns {module:ui/button/buttonview~ButtonView} The button view instance.
	 */
	_createButton( label, icon, className, eventName ) {
		const button = new ButtonView( this.locale );

		button.set( {
			label,
			icon,
			tooltip: true
		} );

		button.extendTemplate( {
			attributes: {
				class: className
			}
		} );

		if ( eventName ) {
			button.delegate( 'execute' ).to( this, eventName );
		}

        if ( className === "ck-button-save") {
            button.isEnabled = false;
        }

		return button;
	}

    /**
	 * Populates the {@link #children} collection of the form.
	 *
	 * @private
	 * @returns {module:ui/viewcollection~ViewCollection} The children of glossary form view.
	 */
	_createFormChildren() {
		const children = this.createCollection();

		children.add( this.videoIdInputView );
		children.add( this.startTimeInputView );
		children.add( this.endTimeInputView );

		children.add( this.saveButtonView );
		children.add( this.cancelButtonView );

		return children;
	}

    /**
	 * Creates a child Input Text View instance for Labeled Field Views.
	 *
	 * @private
	 * @param {module:ui/labeledfield/labeledfieldview~LabeledFieldView} labeledFieldView The labeled field view instance.
	 * @param {String} viewUid The view's unique identifier.
	 * @param {String} statusUid The status's unique identifier.
	 * @returns {module:ui/inputtext/inputtextview~InputTextView} The input text view instance.
	 */
    _createLabeledInputText = ( labeledFieldView, viewUid, statusUid ) => {
        const inputView = new InputTextView( labeledFieldView.locale );
    
        /**
         * Inherited functionality from original createLabeledInputText function
         */
        inputView.set( {
            id: viewUid,
            ariaDescribedById: statusUid
        } );
    
        inputView.bind( 'isReadOnly' ).to( labeledFieldView, 'isEnabled', value => !value );
        inputView.bind( 'hasError' ).to( labeledFieldView, 'errorText', value => !!value );

        inputView.on( 'input', () => {
            // UX: Make the error text disappear and disable the error indicator as the user
            // starts fixing the errors.
            labeledFieldView.errorText = null;
        } );

        inputView.bind( 'input' ).to(labeledFieldView)
        
        /** 
         * Added functionality to listen on `input` event and update preview on change.
         */
        labeledFieldView.listenTo(inputView, 'input', () => { 
            [...this.resultView].forEach( item => {
                this.resultView.remove(item)
            })

			const { value: videoId } = this.videoIdInputView.fieldView.element;
			const { value: startTime } = this.startTimeInputView.fieldView.element;
			const { value: endTime } = this.endTimeInputView.fieldView.element;

            this.resultView.add( 
                new PreviewView( 
                    this.locale, 
                    { 
                        videoId, 
                        startTime: this.timeToSeconds(startTime), 
                        endTime: this.timeToSeconds(endTime) 
                    },
                    this.saveButtonView 
                )
            )

            this.dialog.view.updatePosition();
        })
    
        labeledFieldView.bind( 'isEmpty', 'isFocused', 'placeholder' ).to( inputView );
    
        return inputView;
    };

    /** 
     * Convert time possibly in hh:mm:ss to seconds 
     * @param {string} time The time in either hh:mm:ss | mm:ss | seconds
     * @returns {number} The converted time in seconds.
     */
    timeToSeconds(time) {
        const maxSeconds = 86399; // 23:59:59
        let _time;
        const timeArray = time.split(":");

        if (timeArray.length <= 3) {
            if (timeArray.length === 3) {
                let [hours, minutes, seconds] = timeArray;
                if (isNaN(parseInt(hours))) hours = "0";
                if (isNaN(parseInt(minutes))) minutes = "0";
                if (isNaN(parseInt(seconds))) seconds = "0";

                let totalSeconds =
                parseInt(hours) * 3600 + parseInt(minutes) * 60 + parseInt(seconds);
                if (totalSeconds > maxSeconds) totalSeconds = maxSeconds;

                _time = totalSeconds;
            } else {
                if (timeArray.length === 2) {
                let [minutes, seconds] = timeArray;
                if (isNaN(parseInt(minutes))) minutes = "0";
                if (isNaN(parseInt(seconds))) seconds = "0";

                let totalSeconds = parseInt(minutes) * 60 + parseInt(seconds);
                if (totalSeconds > maxSeconds) totalSeconds = maxSeconds;

                _time = totalSeconds;
                } else {
                let [seconds] = timeArray;
                if (isNaN(parseInt(seconds))) seconds = "0";

                let totalSeconds = parseInt(seconds);
                if (parseInt(seconds) > maxSeconds) totalSeconds = maxSeconds;

                _time = totalSeconds;
                }
            }
        }

        return _time;
    };
} 

/**
 * Fired when the form view is submitted (when one of the children triggered the submit event),
 * for example with a click on {@link #saveButtonView}.
 *
 * @event submit
 */

/**
 * Fired when the form view is canceled, for example with a click on {@link #cancelButtonView}.
 *
 * @event cancel
 */
